/**
 * Créer ou modifier un refuge, un camping (ou une sous-organisation) avec le formulaire du site,
 * et voir l'historique des versions à côté.
 */
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { LocationManagementForm } from '@web/features/organizations/components/LocationManagementForm'
import {
  useLocationVersions,
  useOrganizationLocation,
} from '@web/features/organizations/hooks/useOrganizationLocations'
import '@web/features/organizations/styles/LocationEditPage.css'
import { useOrg } from '../utils/orgContext'
import { formatDate } from '../utils/format'
import { EDITABLE_TYPES } from '../utils/routes'

export function LieuEditPage() {
  const { t } = useTranslation('gestion')
  const { orgId } = useOrg()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { type: typeParam, locationId } = useParams()
  const [search] = useSearchParams()
  const isNew = !locationId
  const wanted = (typeParam ?? search.get('type') ?? 'refuge').toLowerCase()
  const type = (EDITABLE_TYPES as readonly string[]).includes(wanted) ? wanted : 'refuge'
  const id = locationId ? Number(locationId) : null

  const { data: location, isLoading, error } = useOrganizationLocation(orgId, id, {
    enabled: !isNew && id !== null,
    locationType: type,
  })
  const { data: versions } = useLocationVersions(orgId, id, { enabled: !isNew && id !== null })

  const back = () => {
    void queryClient.invalidateQueries({ queryKey: ['insights', orgId] })
    navigate(`/o/${orgId}/lieux`)
  }

  return (
    <div className="g-page g-page--wide">
      <div className="g-page__head">
        <div>
          <Link to={`/o/${orgId}/lieux`} className="g-back">
            <span className="mdi mdi-arrow-left" aria-hidden /> {t('lieux.titre')}
          </Link>
          <h1 className="g-page__title">
            {isNew ? t(`lieux.nouveau_${type}`, { defaultValue: t('lieux.nouveau') }) : (location?.name ?? '…')}
          </h1>
        </div>
      </div>

      {!isNew && isLoading && <p className="g-muted">{t('commun.chargement')}</p>}
      {!isNew && error && <p className="g-alert g-alert--error">{error.message}</p>}

      {(isNew || location) && (
        <div className="g-edit">
          <div className="g-edit__form g-embed">
            <LocationManagementForm
              key={location?.id ?? `new-${type}`}
              organizationId={orgId}
              location={location}
              defaultType={type}
              onSuccess={back}
              onCancel={back}
            />
          </div>
          {!isNew && versions && versions.length > 0 && (
            <aside className="g-card g-edit__aside">
              <h2 className="g-card__title">{t('lieux.historique')}</h2>
              <ol className="g-versions">
                {versions.map((v) => (
                  <li key={v.id}>
                    <strong>v{v.version_number}</strong>{' '}
                    <span className="g-muted">
                      {v.is_manager_edit ? t('lieux.par_gestionnaire') : t('lieux.contribution')} · {formatDate(v.created_at)}
                    </span>
                  </li>
                ))}
              </ol>
            </aside>
          )}
        </div>
      )}
    </div>
  )
}
